import {Hero, Grid, Box, Card} from '@primer/react-brand-next'

import {getImageSources, MAX_CONTENT_WIDTH} from '@github-ui/swp-core/lib/utils/images'

import type {CSSProperties} from 'react'
import type {FlexSuiteImageCardsHeroProps} from './types'
import {getDescription, getHeading} from './utils'

/**
 * Image Cards hero: center-aligned hero with a row of image cards below.
 * Each card renders an image, heading, description, and CTA link
 * using the `imageLinkItem` Contentful content type.
 */
export function FlexSuiteHeroImageCards({fields}: {fields: FlexSuiteImageCardsHeroProps}) {
  const {
    heroHeading,
    heroLabel,
    heroDescription,
    heroCtaOne,
    heroCtaTwo,
    heroTertiaryDescription,
    heroImageCards,
    options,
  } = fields
  const {variant, enableAnimation, align} = options
  const trailingComponent = heroTertiaryDescription ? () => getDescription(heroTertiaryDescription) : undefined

  return (
    <Box
      data-testid="hero-image-cards"
      marginBlockEnd={64}
      borderBlockEndWidth="thin"
      borderStyle="solid"
      borderColor="muted"
    >
      <Hero variant={variant} align={align} enableAnimation={enableAnimation} trailingComponent={trailingComponent}>
        {heroLabel && <Hero.Label>{heroLabel}</Hero.Label>}
        <Hero.Heading>{getHeading(heroHeading)}</Hero.Heading>
        <Hero.Description>{heroDescription ? getDescription(heroDescription) : null}</Hero.Description>
        <Hero.PrimaryAction href={heroCtaOne.fields.href}>{heroCtaOne.fields.text}</Hero.PrimaryAction>
        {heroCtaTwo && (
          <Hero.SecondaryAction href={heroCtaTwo.fields.href}>{heroCtaTwo.fields.text}</Hero.SecondaryAction>
        )}
      </Hero>

      {heroImageCards && heroImageCards.length > 0 && (
        <Grid style={{paddingInline: 0}}>
          <Grid.Column>
            <Box
              borderColor="muted"
              borderStyle="solid"
              borderBlockStartWidth="thin"
              borderInlineEndWidth={{regular: 'thin'}}
              borderInlineStartWidth={{regular: 'thin'}}
            >
              <Grid
                style={
                  {
                    '--brand-Grid-spacing-row': '0',
                    '--brand-Grid-spacing-column-gap': '0',
                  } as CSSProperties
                }
              >
                {heroImageCards.map((card, index) => {
                  const isLast = index === heroImageCards.length - 1

                  return (
                    <Grid.Column key={card.sys.id} span={{xsmall: 12, large: 6}}>
                      <Box
                        padding={{narrow: 32, regular: 48}}
                        borderColor="muted"
                        borderStyle="solid"
                        borderBlockEndWidth={{narrow: isLast ? 'none' : 'thin', wide: 'none'}}
                        borderInlineEndWidth={{wide: isLast ? 'none' : 'thin'}}
                      >
                        <Card href={card.fields.href} variant="minimal" fullWidth ctaText={card.fields.label}>
                          <Card.Image
                            src={`${card.fields.image.fields.file.url}?fm=webp`}
                            alt={card.fields.image.fields.description ?? ''}
                            sources={getImageSources(card.fields.image.fields.file.url, {
                              maxWidth: MAX_CONTENT_WIDTH / 2,
                            })}
                          />
                          <Card.Heading>{getHeading(card.fields.heading)}</Card.Heading>
                          <Card.Description>{getDescription(card.fields.description)}</Card.Description>
                        </Card>
                      </Box>
                    </Grid.Column>
                  )
                })}
              </Grid>
            </Box>
          </Grid.Column>
        </Grid>
      )}
    </Box>
  )
}
